import { ref, type Ref, computed } from 'vue';
import { cloneDeep } from 'lodash';
import { ElLoading } from 'element-plus';
import Moment from 'moment-timezone';
import Swal from 'sweetalert2';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useDashboard } from '@/use/app';
import { useAddress } from '@/use/address';
import { useCustomer } from '@/use/customer';
import { sendGet } from '@/Utils/api';

import { rideServiceTypeList } from '@/constants/app';
import { medicalPlaceTypes } from '@/constants/address';
import { minAge } from '@/constants/caller';
import { type OrderDataType, MedicalTripResult, FulfillmentIndicator } from '@/models/ride';
import { CanceledByType } from '@/gql/graphql';
import type { GeoLocationType, CustomLocationType, SubsidyLocation } from '@/models/address';
import { PartnerCodeName } from '@/models/partner';
import { scheduledRideService } from '@/services/scheduledRide';
import { partnerService } from '@/services/partners';
import { partnerGeofenceService } from '@/services/partners/geofences';
import { addressService } from '@/services/address';

const rideId = ref(0);
const requestId = ref('');
const scheduledRideId = ref(0);
const duplicatedScheduledRide = ref(null as any);
const rideOrderData: Ref<OrderDataType | null> = ref(null);

export const useRide = () => {
  const { showConfirmPopupDialog, showConfirmPopupDialogWithValidation, showErrorPopupDialog, showWarningPopupDialog } = useSwalDialog();
  const { isFamilyDashboard } = useDashboard();
  const { compareDistances } = useAddress();
  const { isDutchessPartnership, isDiamondBarPartnership, isRotcPartnership, customerPartnerSubsidyLocations } = useCustomer();

  const availableServiceTypeList = computed(() => {
    if (isFamilyDashboard.value) {
      return rideServiceTypeList.filter((item, index) => index === 0);
    }

    return rideServiceTypeList;
  });

  const setRideId = (value: number) => {
    rideId.value = value;
  };

  const setRideRequestId = (value: string) => {
    requestId.value = value;
  };

  const setScheduledRideId = (value: number) => {
    scheduledRideId.value = value;
  };

  const setDuplicatedScheduledRide = (value: any) => {
    duplicatedScheduledRide.value = cloneDeep(value);
  };

  const setRideOrderData = (value: OrderDataType | null) => {
    rideOrderData.value = cloneDeep(value);
  };

  const checkMedicationName = async () => {
    const isManualEnter = await showConfirmPopupDialog({
      title: 'What is the name of the medication?',
      confirmButtonText: 'Enter Medication Name',
      cancelButtonText: "Doesn't Know",
    });

    if (!isManualEnter) {
      return;
    }

    const medicationName = await showConfirmPopupDialogWithValidation({
      title: 'What is the name of the medication?',
      input: 'text',
      inputPlaceholder: 'Please input name of the medication',
      validationText: 'Please input name of the medication',
      confirmButtonText: 'Done',
      cancelButtonText: 'Back',
      preConfirm: (value: string) => {
        const restrictedKeywords = [
          'None',
          'N/A',
          'Not Applicable',
          'Refused',
          'Customer doesn’t want to provide',
          'Dont know',
          'Don’t know',
          'DK',
        ];

        const hasRestrictedKeyword = restrictedKeywords.some((keyword) =>
          value.trim().toLocaleLowerCase().includes(keyword.toLocaleLowerCase()),
        );

        if (hasRestrictedKeyword) {
          Swal.showValidationMessage('Please enter a valid value. "None", "N/A", "Don\'t know", or similar responses are not allowed.');
        }

        return value;
      },
    });

    if (!medicationName) {
      return checkMedicationName();
    }

    return medicationName;
  };

  const checkPharmacyQuestions = async (isOrderRideNow: boolean = true) => {
    let notes: string;

    if (isOrderRideNow) {
      const isStorePickupLocationOpen = await showConfirmPopupDialog({
        title: 'Is the store or pickup location open?',
      });

      if (isStorePickupLocationOpen) {
        const isBusinessHoursVerified = await showConfirmPopupDialog({
          title: 'Please verify business hours using Google or your tools.',
          confirmButtonText: 'Confirmed',
          cancelButtonText: 'Back',
        });

        if (!isBusinessHoursVerified) {
          return checkPharmacyQuestions(isOrderRideNow);
        }
      } else {
        showWarningPopupDialog({
          text: 'Offer to schedule pickup during business hours.',
        });

        return;
      }
    }

    const storeName = await showConfirmPopupDialogWithValidation({
      title: 'What is the name of the store?',
      input: 'text',
      inputPlaceholder: 'Please input name of the store',
      validationText: 'Please input name of the store',
      confirmButtonText: 'Done',
      cancelButtonText: "Doesn't Know",
    });

    if (!storeName) {
      showWarningPopupDialog({
        text: 'Please confirm the store name using Google Maps or your tools.',
      });

      return;
    }

    notes = `What is the name of the store? - ${storeName}\n`;

    const medicationName = await checkMedicationName();

    if (!medicationName) {
      showWarningPopupDialog({
        text: 'Please have the customer confirm the prescription name with the store first.',
      });

      return;
    }

    notes += `What is the name of the medication? - ${medicationName}\n`;

    const isControlledSubstance = await showConfirmPopupDialog({
      title: 'Is this medication a controlled substance or narcotic?',
    });

    if (isControlledSubstance) {
      showWarningPopupDialog({
        text: 'Ask the customer if they would like a trip to the pharmacy. We cannot deliver controlled substances or narcotics but can provide a ride.',
      });

      return;
    }

    notes += 'Is this medication a controlled substance or narcotic? - No\n';

    const isItemPaid = await showConfirmPopupDialog({
      title: 'Has the medication/item been paid for?',
      confirmButtonText: 'Paid',
      cancelButtonText: 'Not Paid',
    });

    if (!isItemPaid) {
      showWarningPopupDialog({
        text: 'Please have the customer complete payment with the store first.',
      });

      return;
    }

    notes += 'Has the medication/item been paid for? - Yes\n';

    const prescriptionCount = await showConfirmPopupDialogWithValidation({
      title: 'How many prescriptions are to be picked up?',
      input: 'number',
      inputPlaceholder: '56',
      validationText: 'Please input prescription count',
      confirmButtonText: 'Done',
      cancelButtonText: "Doesn't Know",
      preConfirm: (value: number) => {
        if (!value) {
          Swal.showValidationMessage('Count is required');
        } else if (value < 1 || value > 100) {
          Swal.showValidationMessage('Count must be between 1 and 100');
        }
        return value;
      },
    });

    if (!prescriptionCount) {
      return;
    }
    notes += `How many prescriptions are to be picked up? - ${prescriptionCount}\n`;

    const nameForItemIntended = await showConfirmPopupDialogWithValidation({
      title: 'For whom is this item intended?',
      input: 'text',
      inputPlaceholder: 'Enter who this item is for',
      validationText: 'Enter who this item is for',
      confirmButtonText: 'Done',
    });

    if (!nameForItemIntended) {
      return;
    }

    notes += `For whom is this item intended? - ${nameForItemIntended}\n`;

    const maxBirthDate = Moment().subtract(minAge, 'years').format('YYYY-MM-DD');
    const dateOfBirth = await showConfirmPopupDialogWithValidation({
      title: 'Date of birth of recipient',
      input: 'date',
      inputPlaceholder: 'Please choose the date of birthday',
      validationText: 'Please choose the date of birthday',
      confirmButtonText: 'Done',
      dateValidation: {
        maxDate: maxBirthDate,
      },
    });

    if (!dateOfBirth) {
      return;
    }

    notes += `Date of birth of recipient - ${dateOfBirth}\n`;

    const pickupWayInputOptions = {
      'Driver needs to enter the store': 'Driver needs to enter the store',
      'Driver can use the drive-thru': 'Driver can use the drive-thru',
      Other: 'Other',
    };

    let pickupWay = await showConfirmPopupDialogWithValidation({
      title: 'How should the prescription be picked up?',
      input: 'radio',
      validationText: 'Please choose one option',
      inputOptions: pickupWayInputOptions,
      confirmButtonText: 'Done',
    });

    if (!pickupWay) {
      return;
    }

    if (pickupWay === 'Other') {
      const otherWayToPickupMedication = await showConfirmPopupDialogWithValidation({
        title: 'Explain how we will need to pick up the medication',
        input: 'text',
        inputPlaceholder: 'Please input the way to pick up the medication',
        validationText: 'Please input the way to pick up the medication',
        confirmButtonText: 'Next',
      });

      if (!otherWayToPickupMedication) {
        return;
      }

      pickupWay = `Other (${otherWayToPickupMedication})`;
    }

    notes += `How should the prescription be picked up? - ${pickupWay}\n`;

    const dropOffMedicationOptions = {
      'Hand it over': 'Hand it over',
      'At the lobby': 'At the lobby',
      'Meet the driver on the sidewalk': 'Meet the driver on the sidewalk',
      Other: 'Other',
    };

    let dropOffMedication = await showConfirmPopupDialogWithValidation({
      title: 'How should the medication be delivered?',
      input: 'radio',
      validationText: 'Please choose one option',
      inputOptions: dropOffMedicationOptions,
      confirmButtonText: 'Done',
    });

    if (!dropOffMedication) {
      return;
    }

    if (dropOffMedication === 'Other') {
      const otherWayToDropoffMedication = await showConfirmPopupDialogWithValidation({
        title: 'Explain how we will drop off the medication?',
        input: 'text',
        inputPlaceholder: 'Please input the way to drop off the medication',
        validationText: 'Please input the way to drop off the medication',
        confirmButtonText: 'Done',
      });

      if (!otherWayToDropoffMedication) {
        return;
      }

      dropOffMedication = `Other (${otherWayToDropoffMedication})`;
    }

    notes += `How should the medication be delivered? - ${dropOffMedication}\n`;

    const additionalNotesResponse = await showConfirmPopupDialogWithValidation({
      title: 'Additional notes for Loss & Prevention',
      input: 'textarea',
      inputPlaceholder: 'Please input any additional notes',
    });

    if (additionalNotesResponse) {
      notes += 'Additional notes for Loss & Prevention\n';
      notes += additionalNotesResponse;
    }

    await showWarningPopupDialog({
      text: 'Remind customer to have their phone ready for callbacks. Confirm that ride duration and cost may vary due to pharmacy wait times.',
    });

    return {
      notes,
    };
  };

  const checkSpecialRequestItemPaidQuestion = async () => {
    const response = await showConfirmPopupDialog({
      title: 'Has the item been paid for?',
      confirmButtonText: 'Paid',
      cancelButtonText: 'Not Paid',
      denyButtonText: 'Payment Not Required',
    });

    if (!response.isDenied) {
      return response;
    }

    const notPaymentRequiredConfirmed = await showConfirmPopupDialog({
      title: 'Please confirm again that this item does not require payment for pickup.',
      confirmButtonText: 'Confirmed',
      cancelButtonText: 'Back',
    });

    if (notPaymentRequiredConfirmed) {
      return response;
    }

    return checkSpecialRequestItemPaidQuestion();
  };

  const checkSpecialRequestPickupInstructionsQuestion = async () => {
    const response = await showConfirmPopupDialogWithValidation({
      title: 'Instructions on how this should be picked up',
      input: 'textarea',
      confirmButtonText: 'Done',
    });

    if (!response) {
      return;
    }

    const isConfirmed = await showConfirmPopupDialog({
      title: 'Please verify that these instructions accurately reflect the steps the driver must follow during pick-up.',
      confirmButtonText: 'Confirmed',
      cancelButtonText: 'Back',
    });

    if (isConfirmed) {
      return response;
    }

    return checkSpecialRequestPickupInstructionsQuestion();
  };

  const checkSpecialRequestQuestions = async (isOrderRideNow: boolean = true) => {
    const response = {} as any;
    let notes: string;

    // Step1
    const isGroceryOrFoodRequest = await showConfirmPopupDialog({
      title: 'Is the request to pick up Food or Grocery items?',
      confirmButtonText: 'Yes, refer to Gourmet/Grocery',
      cancelButtonText: 'Special Request via LP',
    });

    if (isGroceryOrFoodRequest) {
      await showWarningPopupDialog({
        text: 'Please offer to transfer the client to the Gourmet or Grocery departments, which have more tools to support this request.',
      });

      return;
    }

    if (isOrderRideNow) {
      // Step2
      const isStorePickupLocationOpen = await showConfirmPopupDialog({
        title: 'Is the store or pickup location open?',
      });

      if (!isStorePickupLocationOpen) {
        await showWarningPopupDialog({
          text: 'Please offer to schedule the request during business hours.',
        });

        return;
      }
    }

    // Step3
    const storeName = await showConfirmPopupDialogWithValidation({
      title: 'What is the name of the store?',
      input: 'text',
      inputPlaceholder: 'Please input name of store',
      validationText: 'Please input name of store',
      confirmButtonText: 'Done',
    });

    if (!storeName) {
      await showWarningPopupDialog({
        text: 'Please confirm the store name using Google Maps or your available tools.',
      });

      return;
    }

    notes = `What is the name of the store? - ${storeName}\n`;

    // Step4
    type PickItemCategory = 'food' | 'grocery' | 'package';
    const pickItemCategoryOptions = {
      food: 'Food',
      grocery: 'Grocery',
      package: 'Package',
    };

    const pickItemCategory: PickItemCategory = await showConfirmPopupDialogWithValidation({
      title: 'What are we picking up?',
      input: 'radio',
      validationText: 'Please choose one option',
      inputOptions: pickItemCategoryOptions,
      confirmButtonText: 'Done',
    });

    if (!pickItemCategory) {
      return;
    }

    // Step5
    const pickItemConfig: Record<
      PickItemCategory,
      {
        title: string;
        placeholder: string;
        validationText: string;
      }
    > = {
      food: {
        title: 'What is the name of the food item? (e.g., pizza, soda)',
        placeholder: 'Please input name of food item',
        validationText: 'Please input name of food item',
      },
      grocery: {
        title: 'What is the name of the grocery item? (e.g., groceries order, cat/dog supplies)',
        placeholder: 'Please input name of grocery item',
        validationText: 'Please input name of grocery item',
      },
      package: {
        title: 'What is the name of the package item? (e.g., clothes, laundry, animal food/medicines)',
        placeholder: 'Please input name of package item',
        validationText: 'Please input name of package item',
      },
    };

    let pickItemName = '';
    const config = pickItemConfig[pickItemCategory];

    if (config) {
      pickItemName = await showConfirmPopupDialogWithValidation({
        title: config.title,
        input: 'text',
        inputPlaceholder: config.placeholder,
        validationText: config.validationText,
        confirmButtonText: 'Done',
        cancelButtonText: "Doesn't Know",
      });
    }

    if (!pickItemName) {
      await showWarningPopupDialog({
        text: `Please have the customer confirm the ${pickItemCategory} item name with the store first.`,
      });

      return;
    }

    notes += `What are we picking up? - ${pickItemName}(${pickItemCategory})\n`;

    // Step6
    const itemPaid = await checkSpecialRequestItemPaidQuestion();
    if (itemPaid.isDismissed) {
      showWarningPopupDialog({
        text: 'Please have the customer complete payment with the store first.',
      });

      return;
    }

    notes += `Has the item been paid for? - ${itemPaid.isConfirmed ? 'Paid' : 'Payment Not Required'}\n`;

    // Step7
    const bagsCount = await showConfirmPopupDialogWithValidation({
      title: 'How many bags is this?',
      input: 'number',
      inputPlaceholder: 'Please input count of bags',
      validationText: 'Please input count of bags',
      confirmButtonText: 'Done',
      cancelButtonText: "Doesn't Know",
    });

    if (!bagsCount) {
      await showWarningPopupDialog({
        text: 'Please have the customer confirm the number of bags with the store so nothing is left behind by the driver.',
      });

      return;
    }

    notes += `How many bags is this? - ${bagsCount}\n`;

    // Step8
    const nameForItemIntended = await showConfirmPopupDialogWithValidation({
      title: 'For whom is this item intended (name please)?',
      input: 'text',
      inputPlaceholder: 'Enter who this item is for',
      validationText: 'Enter who this item is for',
      confirmButtonText: 'Done',
    });

    if (!nameForItemIntended) {
      return;
    }

    notes += `For whom is this item intended? - ${nameForItemIntended}\n`;

    // Step9
    const pickupInstructions = await checkSpecialRequestPickupInstructionsQuestion();

    if (!pickupInstructions) {
      return;
    }

    notes += `Instructions on how this should be picked up - ${pickupInstructions}\n`;

    // Step10
    const pickupMethodInputOptions = {
      'Driver needs to enter the store': 'Driver needs to enter the store',
      'Driver can use the Drive Thru': 'Driver can use the Drive Thru',
      Other: 'Other',
    };

    let pickupWay = await showConfirmPopupDialogWithValidation({
      title: 'How should this be picked up?',
      input: 'radio',
      validationText: 'Please choose one option',
      inputOptions: pickupMethodInputOptions,
      confirmButtonText: 'Done',
    });

    if (!pickupWay) {
      return;
    }

    if (pickupWay === 'Other') {
      const otherWayToPickupItem = await showConfirmPopupDialogWithValidation({
        title: 'Explain how we will need to pick up the item',
        input: 'text',
        inputPlaceholder: 'Please input the way to pick up the item',
        validationText: 'Please input the way to pick up the item',
        confirmButtonText: 'Done',
      });

      if (!otherWayToPickupItem) {
        return;
      }

      pickupWay = `Other (${otherWayToPickupItem})`;
    }

    notes += `How should this be picked up? - ${pickupWay}\n`;

    // Step11
    const dropOffItemOptions = {
      'Hand It Over': 'Hand it over',
      'At The Lobby': 'At The Lobby',
      'Will Meet The Driver On The Sidewalk': 'Will Meet The Driver On The Sidewalk',
      Other: 'Other',
    };

    let dropOffItem = await showConfirmPopupDialogWithValidation({
      title: 'How do we drop off the item?',
      input: 'radio',
      validationText: 'Please choose one option',
      inputOptions: dropOffItemOptions,
      confirmButtonText: 'Done',
    });

    if (!dropOffItem) {
      return;
    }

    if (dropOffItem === 'Other') {
      const otherWayToDropoffItem = await showConfirmPopupDialogWithValidation({
        title: 'Explain how we will drop off the item?',
        input: 'text',
        inputPlaceholder: 'Please input the way to drop off the item',
        validationText: 'Please input the way to drop off the item',
        confirmButtonText: 'Next',
      });

      if (!otherWayToDropoffItem) {
        return;
      }

      dropOffItem = `Other (${otherWayToDropoffItem})`;
    }

    notes += `How do we drop off the item?: ${dropOffItem}\n`;

    // Step12
    const additionalNotesResponse = await showConfirmPopupDialogWithValidation({
      title: 'Additional notes for Loss & Prevention',
      input: 'textarea',
      inputPlaceholder: 'Please input any additional notes',
      confirmButtonText: 'Done',
    });

    if (additionalNotesResponse) {
      notes += `Additional notes for Loss & Prevention: ${additionalNotesResponse}\n`;
    }

    await showWarningPopupDialog({
      text: 'Remind the customer to keep their phone available for callbacks. Confirm that ride duration and cost may vary depending on store wait times.',
    });

    response.notes = notes;

    return response;
  };

  const doCancelScheduledRide = async (
    scheduledRideId: number,
    cancellationReason: string,
    canceledByType: CanceledByType = CanceledByType.Operator,
    isReschedule = false,
  ): Promise<boolean> => {
    const loading = ElLoading.service({
      lock: true,
      text: 'Canceling Scheduled Ride...',
      background: 'rgba(0, 0, 0, 0.7)',
      customClass: 'custom-full-spinner-class',
    });

    try {
      const result = await scheduledRideService.cancelScheduledRide({
        scheduledRideId,
        cancellationReason,
        canceledByType,
        isReschedule,
      });

      return result;
    } catch (error) {
      const errorText = util.getGraphqlError(error, false);
      showErrorPopupDialog({
        text: errorText,
      });
    } finally {
      loading.close();
    }

    return false;
  };

  const updateRepeatedScheduledRide = async (scheduledRide: any): Promise<boolean> => {
    const updatedData = {
      phoneNumber: scheduledRide.riderPhone,
      riderPhone: scheduledRide.riderPhone,
      callToConfirmSurge: scheduledRide.callToConfirmSurge ? 1 : 0,
      rideName: scheduledRide.rideName,
      rideType: scheduledRide.rideType,
      rideMultiple: true,
      pickupTime: scheduledRide.pickupTime,
      pickupDate: scheduledRide.pickupDate,
      repeatEveryXWeek: scheduledRide.repeatEveryXWeek,
      streetAddress1: scheduledRide.streetAddress1,
      city1: scheduledRide.city1,
      state1: scheduledRide.state1,
      zip1: scheduledRide.zip1,
      lat1: scheduledRide.lat1,
      long1: scheduledRide.long1,
      streetAddress2: scheduledRide.streetAddress2,
      city2: scheduledRide.city2,
      state2: scheduledRide.state2,
      zip2: scheduledRide.zip2,
      lat2: scheduledRide.lat2,
      long2: scheduledRide.long2,
      noteToDriver: scheduledRide.noteToDriver,
      day0: scheduledRide.day0,
      day1: scheduledRide.day1,
      day2: scheduledRide.day2,
      day3: scheduledRide.day3,
      day4: scheduledRide.day4,
      day5: scheduledRide.day5,
      day6: scheduledRide.day6,
      endOn: scheduledRide.endOn,
      endAfterXTimes: scheduledRide.endAfterXTimes,
      timeZone: scheduledRide.timeZone,
      overrideBigCar: scheduledRide.overrideBigCar ? 1 : 0,
      overrideSmallCar: scheduledRide.overrideSmallCar ? 1 : 0,
      overrideBannedCar: scheduledRide.overrideBannedCar ? 1 : 0,
      overrideBadDriver: scheduledRide.overrideBadDriver ? 1 : 0,
      skipScheduledRideDates: scheduledRide.skipScheduledRideDates,
    };

    const loading = ElLoading.service({
      lock: true,
      text: 'Updating skip dates...',
      background: 'rgba(0, 0, 0, 0.7)',
      customClass: 'custom-full-spinner-class',
    });

    let success = false;
    try {
      await scheduledRideService.updateScheduledRide({
        scheduledRideId: scheduledRide.id,
        data: updatedData,
      });

      success = true;
    } catch (error) {
      const errorText = util.getGraphqlError(error, false);
      showErrorPopupDialog({
        text: errorText,
      });
    }
    loading.close();

    return success;
  };

  const getCompletedLastRideAddress = async (callerId: string) => {
    try {
      const response = await sendGet(`callers/${callerId}/lastLocation`);

      if (!response.data.streetAddress) {
        return null;
      }

      return {
        streetAddress: response.data.streetAddress || '',
        streetAddress1: '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip: response.data.zip || '',
        name: '',
        latitude: response.data.lat ? Number(response.data.lat) : null,
        longitude: response.data.lng ? Number(response.data.lng) : null,
        googlePlaceId: null,
      };
    } catch (error) {
      return null;
    }
  };

  const validatePartnershipGeofence = async (geofenceId: number, point: GeoLocationType) => {
    try {
      const result = await partnerService.partnerGeofenceContains({
        id: geofenceId,
        point: {
          lat: point.lat!,
          lng: point.lng!,
        },
      });

      return result;
    } catch (error: any) {
      return false;
    }
  };

  const checkAddressIsSubsidyLocation = (subsidyLocations: SubsidyLocation[], address: CustomLocationType) => {
    const defaultMinimumDistanceInMiles = 0.033;
    // .033 miles = 53 meters

    let isSubsidyLocation = false;

    for (const subsidyLocation of subsidyLocations) {
      const radiusInMiles = Number(subsidyLocation.radius || defaultMinimumDistanceInMiles);

      if (subsidyLocation.latitude && subsidyLocation.longitude && address.latitude && address.longitude) {
        isSubsidyLocation = compareDistances(
          subsidyLocation.latitude,
          subsidyLocation.longitude,
          address.latitude,
          address.longitude,
          radiusInMiles,
          '<=',
        );
      } else if (subsidyLocation.zip && address.zip) {
        isSubsidyLocation = subsidyLocation.zip === address.zip;
      }

      if (isSubsidyLocation) {
        break;
      }
    }

    return isSubsidyLocation;
  };

  /*
    Special case: Diamond Bar partnership ride flow
    Show popup if:
      - One of PU or DO is in serviceArea1 AND the other is in serviceArea2 but outside serviceArea1
      - OR one of PU or DO is in serviceArea1 AND the other is at a subsidy location

    Note: serviceArea2 includes serviceArea1

    Please note that this code is not part of the regular flow — it's a special case implemented specifically for the Diamond Bar partnership ride flow.
  */
  const checkNeedToShowMedicalPopupForDiamondBarPartnership = async (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    const partnerCode = PartnerCodeName.DIAMOND_BAR;

    // No need to check
    if (!startAddress.latitude || !startAddress.longitude || !endAddress.latitude || !endAddress.longitude) {
      return false;
    }

    try {
      const response = await partnerGeofenceService.fetchPartnerGeofences(partnerCode);

      const serviceArea1 = response.find((item) => item.target === 'enrollment');
      const serviceArea2 = response.find((item) => item.target === 'subsidy');

      // No need to show popup if there is no service area
      if (!serviceArea1 || !serviceArea2) {
        return false;
      }

      const checkLocationStatus = async (address: CustomLocationType) => {
        const inArea1 = await validatePartnershipGeofence(serviceArea1.id, {
          lat: address.latitude!,
          lng: address.longitude!,
        });

        const inArea2 = await validatePartnershipGeofence(serviceArea2.id, {
          lat: address.latitude!,
          lng: address.longitude!,
        });

        const atSubsidy = checkAddressIsSubsidyLocation(customerPartnerSubsidyLocations.value, address);

        return { inArea1, inArea2, atSubsidy };
      };

      const [puStatus, doStatus] = await Promise.all([checkLocationStatus(startAddress), checkLocationStatus(endAddress)]);

      const isPUOutOfGeofence = !puStatus.inArea1 && !puStatus.inArea2 && !puStatus.atSubsidy;
      const isDOOutOfGeofence = !doStatus.inArea1 && !doStatus.inArea2 && !doStatus.atSubsidy;

      if (isPUOutOfGeofence || isDOOutOfGeofence) {
        await showOutOfGeofencePopupForDiamondBarPartnership();
        return false;
      }

      const puInArea2Only = puStatus.inArea2 && !puStatus.inArea1;
      const doInArea2Only = doStatus.inArea2 && !doStatus.inArea1;

      const puSubsidyOutsideArea1 = puStatus.atSubsidy && !puStatus.inArea1;
      const doSubsidyOutsideArea1 = doStatus.atSubsidy && !doStatus.inArea1;

      // Case 1: Area1 <-> Area2 (but not Area1)
      const area1AndArea2 = (puStatus.inArea1 && doInArea2Only) || (doStatus.inArea1 && puInArea2Only);

      // Case 2: Area1 <-> Subsidy (subsidy NOT in Area1)
      const area1AndSubsidy = (puStatus.inArea1 && doSubsidyOutsideArea1) || (doStatus.inArea1 && puSubsidyOutsideArea1);

      return area1AndArea2 || area1AndSubsidy;
    } catch (err) {
      return false;
    }
  };

  /*
    One of PU or DO should be in subsidy location and another should be out.
  */
  const checkNeedToShowMedicalPopupForRotcPartnership = async (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    const rotcPartnershipCodes = ['rotc', 'rotcgo'];
    const subsidyLocations: SubsidyLocation[] = [];

    for (const codeName of rotcPartnershipCodes) {
      try {
        const partner = await partnerService.fetchPartner({ codeName });
        subsidyLocations.push(...(partner?.subsidyLocations ?? []));
      } catch (err) {
        // No need to do additional action
      }
    }

    const isPUAtSubsidyLocation = checkAddressIsSubsidyLocation(subsidyLocations, startAddress);
    const isDOAtSubsidyLocation = checkAddressIsSubsidyLocation(subsidyLocations, endAddress);

    return isPUAtSubsidyLocation !== isDOAtSubsidyLocation;
  };

  const validateLocationNames = (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    if (startAddress.name && endAddress.name) {
      return true;
    }

    showWarningPopupDialog({
      text: 'Please add the location name to the request',
    });

    return false;
  };

  const showMedicalLocationVerifyPopup = async () => {
    return showConfirmPopupDialog({
      text: 'Have you been able to verify this is the correct trip type and address?',
      title: 'Neither location is showing as a medical facility in our system.',
    });
  };

  const checkMedicalLocationWithPlaceTypes = async (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    let googlePlaceTypesForPU = startAddress.googlePlaceTypes ?? [];
    let googlePlaceTypesForDO = endAddress.googlePlaceTypes ?? [];

    if (googlePlaceTypesForPU.length === 0) {
      googlePlaceTypesForPU = await addressService.getPlaceTypesByGeocode(startAddress.latitude!, startAddress.longitude!);
    }

    if (googlePlaceTypesForDO.length === 0) {
      googlePlaceTypesForDO = await addressService.getPlaceTypesByGeocode(endAddress.latitude!, endAddress.longitude!);
    }

    const isPUMedicalPlace = googlePlaceTypesForPU.some((type) => medicalPlaceTypes.includes(type));
    const isDOMedicalPlace = googlePlaceTypesForDO.some((type) => medicalPlaceTypes.includes(type));

    return isPUMedicalPlace || isDOMedicalPlace;
  };

  const checkMedicalPlaces = async (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    const isMedicaLocation = await checkMedicalLocationWithPlaceTypes(startAddress, endAddress);

    if (isMedicaLocation) {
      return true;
    }

    const isMedicalLocationVerified = await showMedicalLocationVerifyPopup();

    return isMedicalLocationVerified;
  };

  const checkMedicalTripForDutchesPartnership = async (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    if (!isDutchessPartnership.value) {
      return MedicalTripResult.NO_VALIDATION_NEEDED;
    }

    const isMedicalTripConfirmed = await showConfirmPopupDialog({
      title: 'Is this to a medical trip within Dutchess County?',
    });

    if (!isMedicalTripConfirmed) {
      return MedicalTripResult.USER_DECLINED;
    }

    const isMedicalTrip = await checkMedicalPlaces(startAddress, endAddress);

    if (!isMedicalTrip) {
      return MedicalTripResult.NO_MEDICAL_PLACE;
    }

    // If trip is medical, location name is required
    const locationValid = await validateLocationNames(startAddress, endAddress);

    return locationValid ? MedicalTripResult.VALIDATION_PASSED : MedicalTripResult.VALIDATION_FAILED;
  };

  const showOutOfGeofencePopupForDiamondBarPartnership = async () => {
    const title = 'This is outside of the Diamond Ride service area';
    const htmlText = `
      <ul class="text-left">
        <li>Do NOT ask for a credit card.</li>
      </ul>
    `;

    return showWarningPopupDialog({
      text: htmlText,
      title,
      type: 'html',
      icon: 'info',
    });
  };

  const checkMedicalTripForDiamondBarPartnership = async (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    if (!isDiamondBarPartnership.value) {
      return MedicalTripResult.NO_VALIDATION_NEEDED;
    }

    const isNeedToShowPopup = await checkNeedToShowMedicalPopupForDiamondBarPartnership(startAddress, endAddress);

    if (!isNeedToShowPopup) {
      return MedicalTripResult.NO_VALIDATION_NEEDED;
    }

    const isMedicalTripConfirmed = await showConfirmPopupDialog({
      title: 'Is this for a medical trip?',
    });

    if (!isMedicalTripConfirmed) {
      await showOutOfGeofencePopupForDiamondBarPartnership();
      return MedicalTripResult.USER_DECLINED;
    }

    const isMedicalTrip = await checkMedicalPlaces(startAddress, endAddress);

    if (!isMedicalTrip) {
      return MedicalTripResult.NO_MEDICAL_PLACE;
    }

    // If trip is medical, location name is required
    const locationValid = await validateLocationNames(startAddress, endAddress);

    return locationValid ? MedicalTripResult.VALIDATION_PASSED : MedicalTripResult.VALIDATION_FAILED;
  };

  const checkMedicalTripForRotcPartnership = async (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    if (!isRotcPartnership.value) {
      return MedicalTripResult.NO_VALIDATION_NEEDED;
    }

    const isNeedToShowPopup = await checkNeedToShowMedicalPopupForRotcPartnership(startAddress, endAddress);

    if (!isNeedToShowPopup) {
      return MedicalTripResult.NO_VALIDATION_NEEDED;
    }

    const isMedicalTrip = await showConfirmPopupDialog({
      title: 'Is this for a medical trip outside of the normal service area?',
    });

    if (!isMedicalTrip) {
      return MedicalTripResult.USER_DECLINED;
    }

    // If trip is medical, location name is required
    const locationValid = await validateLocationNames(startAddress, endAddress);

    return locationValid ? MedicalTripResult.VALIDATION_PASSED : MedicalTripResult.VALIDATION_FAILED;
  };

  const showLowDriverAvailabilityMessage = (fulfillmentIndicator?: string) => {
    if (!fulfillmentIndicator) {
      return;
    }

    if (fulfillmentIndicator.toLocaleLowerCase() !== FulfillmentIndicator.RED) {
      return;
    }

    const warningText = [
      'Please inform the customer that we expect low driver availability at this time.',
      'If it is a critical appointment, suggest scheduling extra early to increase pickup probability or finding alternate transportation.',
    ].join(' ');

    return showWarningPopupDialog({
      title: 'Low Driver Availability Expected',
      text: warningText,
    });
  };

  return {
    rideId,
    requestId,
    scheduledRideId,
    duplicatedScheduledRide,
    rideOrderData,
    availableServiceTypeList,

    setRideId,
    setRideRequestId,
    setScheduledRideId,
    setDuplicatedScheduledRide,
    setRideOrderData,
    checkPharmacyQuestions,
    checkSpecialRequestQuestions,
    doCancelScheduledRide,
    updateRepeatedScheduledRide,
    getCompletedLastRideAddress,
    checkNeedToShowMedicalPopupForDiamondBarPartnership,
    checkNeedToShowMedicalPopupForRotcPartnership,
    checkMedicalTripForDutchesPartnership,
    checkMedicalTripForDiamondBarPartnership,
    checkMedicalTripForRotcPartnership,
    showLowDriverAvailabilityMessage,
  };
};
